import { SharedSlice } from 'src/components/slice'
import { StateCreator } from 'zustand'
import { PackageDetail } from '../_common/interfaces'
import { Dayjs } from 'dayjs'
import { mercuryApi } from '$api'
import useAppStore from '$appStore'
import { exceptionMessageHandler, logger, PermissionType } from '$utils'
import { enqueueLoadedSnackbar } from 'src/utils/snackbar'
import {
  IClockinResponse,
  ISalaryWeekContainer,
  IStaffMemberDetail,
  ReportsMetadata,
} from '$models'
import {
  remunerationTimelineService,
  staffMemberService,
  timeClockinService,
} from '$services'
import { RemunerationType, ShiftType } from '$constants'

interface TimeclockSharedState {
  allClockins: IClockinResponse[]
  staffMembers: IStaffMemberDetail[]
  allPackages: PackageDetail[]
  salaryWeekContainers: { [key: string]: ISalaryWeekContainer[] }
}

export interface TimeclockSharedSlice extends TimeclockSharedState {
  setAllClockins: (newData: IClockinResponse[]) => void
  setStaffMembers: (newData: IStaffMemberDetail[]) => void
  setAllPackages: (newData: PackageDetail[]) => void

  /**
   * Resets Timeclock Shared Slice state
   */
  resetTimeclockSharedSlice: () => void

  /**
   * Gets time clockins within a certain time range
   * @param startTime the start of the time range
   * @param endTime the end of the time range
   * @param includeInactive whether or not to include inactive clocks
   * @param shiftTypesToInclude optional list of shift types to filter by
   * @param staffMemberIds the ids of the staff members to get the clocks for
   * @param shouldTruncateShifts Denotes if we should truncate the shifts or not
   */
  getTimeClockins: (
    startDateTime: Dayjs,
    endDateTime: Dayjs,
    includeInactive: boolean,
    shiftTypesToInclude?: ShiftType[],
    staffMemberIds?: string[],
    shouldTruncateShifts?: boolean
  ) => Promise<IClockinResponse[]>

  /**
   * Get Staff Members
   * @param time Today's date (not UTC)
   */
  getStaffMembers: (time?: Dayjs) => Promise<void>

  /**
   * Get All Packages
   */
  getPackages: () => Promise<void>

  /**
   * Update ClockinDetail
   * @param clockinDetail ClockinDetail to update
   * @param reportsMetadata Reports metadata (date and date range filters)
   * @param shiftTypesToInclude Optional list of shift types to filter by on Clockins re-fetch
   * @param includeInactive Whether to include inactive clocks on Clockins fetch
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   * @param setRowLoading Optional callback to set row-specific loading state
   */
  updateClockinDetail: (
    clockinDetail: IClockinResponse,
    reportsMetadata?: ReportsMetadata,
    shiftTypesToInclude?: ShiftType[],
    includeInactive?: boolean,
    successMessage?: string,
    errorMessage?: string,
    setRowLoading?: (isLoading: boolean) => void
  ) => Promise<boolean>

  /**
   * Get Salary Week Containers
   * @param reportStartDate the start of the time range
   * @param reportEndDate the end of the time range
   * @param staffMemberIds the ids of the staff members to get the salary week containers for
   * @returns Promise<void>
   */
  getSalaryWeekContainers: (
    reportStartDate: Dayjs,
    reportEndDate: Dayjs,
    staffMemberIds?: string[]
  ) => Promise<void>
}

const initialState: TimeclockSharedState = {
  allClockins: [],
  staffMembers: [],
  allPackages: [],
  salaryWeekContainers: {},
}

const createPunchesByStaffSlice: StateCreator<
  TimeclockSharedSlice & SharedSlice,
  [],
  [],
  TimeclockSharedSlice
> = (set, get) => ({
  // State variables
  ...initialState,

  // State setters
  setAllClockins: (newData) => set(() => ({ allClockins: newData })),
  setStaffMembers: (newData) => set(() => ({ staffMembers: newData })),
  setAllPackages: (newData) => set(() => ({ allPackages: newData })),
  resetTimeclockSharedSlice: () => set(initialState),

  // State API calls
  getTimeClockins: async (
    startDateTime: Dayjs,
    endDateTime: Dayjs,
    includeInactive: boolean,
    shiftTypesToInclude?: ShiftType[],
    staffMemberIds?: string[],
    shouldTruncateShifts: boolean = true
  ): Promise<IClockinResponse[]> => {
    return timeClockinService
      .getClockins(
        startDateTime,
        endDateTime,
        includeInactive,
        shiftTypesToInclude,
        staffMemberIds,
        shouldTruncateShifts
      )
      .then((result) => {
        if (staffMemberIds && staffMemberIds.length > 0) {
          // If specific staff member IDs are provided, merge with existing clockins
          set((state) => {
            // Filter out existing clockins for the specified staff members
            const filteredExistingClockins = state.allClockins.filter(
              (clockin) => !staffMemberIds.includes(clockin.staffMemberId!)
            )

            // Combine filtered existing clockins with new results
            const updatedClockins = [...filteredExistingClockins, ...result]

            return { allClockins: updatedClockins }
          })
        } else {
          // If no specific staff member IDs, replace all clockins
          set(() => ({ allClockins: result }))
        }
        return result
      })
      .catch((error) => {
        set(() => ({
          allClockins: [],
        }))
        exceptionMessageHandler(error, 'Failed to retrieve time clockins')
        return []
      })
  },

  getStaffMembers: async (time?: Dayjs): Promise<void> => {
    return staffMemberService
      .getAll(time, true, false)
      .then((result) => set(() => ({ staffMembers: result })))
      .catch((error) => {
        set(() => ({
          staffMembers: [],
        }))
        exceptionMessageHandler(error, 'Failed to retrieve staff members')
      })
  },

  getPackages: async (): Promise<void> => {
    const { activeCompany } = useAppStore.getState()

    const hasPayrollPermission = activeCompany?.hasPermission(
      PermissionType.Payroll
    )

    if (!hasPayrollPermission) {
      return set(() => ({ allPackages: [] }))
    }

    return mercuryApi
      .get<PackageDetail[]>(`/pay-rules/packages`)
      .then((result) => set(() => ({ allPackages: result })))
      .catch((error) => {
        set(() => ({
          allPackages: [],
        }))
        exceptionMessageHandler(error, 'Failed to retrieve staff members')
      })
  },

  updateClockinDetail: async (
    clockinDetail: IClockinResponse,
    reportsMetadata?: ReportsMetadata,
    shiftTypesToInclude?: ShiftType[],
    includeInactive: boolean = false,
    successMessage?: string,
    errorMessage?: string,
    setRowLoading?: (isLoading: boolean) => void
  ) => {
    const existingClockinDetail = get().allClockins.find(
      (clockin) => clockin.id === clockinDetail.id
    )

    if (!existingClockinDetail) {
      enqueueLoadedSnackbar(
        'Failed to find the clockin entry to update',
        'error'
      )
      return false
    }

    try {
      if (setRowLoading) {
        setRowLoading(true)
      } else {
        set(() => ({ isLoading: true }))
      }

      // TAKEN FROM ANGULAR:
      // Small hack for getting around the problem where changing the hourlyRate updates
      // the newClock's hourlyRate from undefined to null.
      // the latter will trigger a validation error
      // See LOADED-1226
      if (clockinDetail.hourlyRate == null) {
        delete clockinDetail.hourlyRate
      }

      const updatedClockinDetail: IClockinResponse =
        await timeClockinService.update(clockinDetail)

      if (!updatedClockinDetail) {
        logger.error(`${errorMessage} - updatedClockinDetail doesn't exist`)
        // Rollback optimistic update
        set((state) => ({
          allClockins: state.allClockins.map((clockin) =>
            clockin.id === existingClockinDetail.id
              ? existingClockinDetail
              : clockin
          ),
        }))

        enqueueLoadedSnackbar(errorMessage, 'error')
        return false
      }

      if (
        reportsMetadata &&
        clockinDetail.remunerationType === RemunerationType.Salary
      ) {
        // We need to fetch the clockins again to update the list, since the new clockin can affect the Rate
        // of other entries. This happens when the clockin belongs to a staff member who had a Salary type
        // remuneration timeline during that period
        await get().getTimeClockins(
          reportsMetadata.startDateTime,
          reportsMetadata.endDateTime,
          includeInactive,
          shiftTypesToInclude,
          [clockinDetail.staffMemberId!]
        )
      } else {
        set((state) => ({
          allClockins: state.allClockins.map((clockin) =>
            clockin.id === updatedClockinDetail.id
              ? updatedClockinDetail
              : clockin
          ),
        }))
      }

      enqueueLoadedSnackbar(successMessage, 'success')
      return true
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)

      // Rollback optimistic update
      set((state) => ({
        allClockins: state.allClockins.map((clockin) =>
          clockin.id === existingClockinDetail.id
            ? existingClockinDetail
            : clockin
        ),
      }))
      return false
    } finally {
      if (setRowLoading) {
        setRowLoading(false)
      } else {
        set(() => ({ isLoading: false }))
      }
    }
  },

  getSalaryWeekContainers: async (
    reportStartDate: Dayjs,
    reportEndDate: Dayjs,
    staffMemberIds?: string[]
  ): Promise<void> => {
    return remunerationTimelineService
      .getSalaryWeekContainers(reportStartDate, reportEndDate, staffMemberIds)
      .then((result) => set(() => ({ salaryWeekContainers: result })))
      .catch((error) => {
        set(() => ({
          salaryWeekContainers: {},
        }))
        exceptionMessageHandler(
          error,
          'Failed to retrieve salaried remuneration information'
        )
      })
  },
})

export default createPunchesByStaffSlice
